import { PAISE } from "../constants/entityType";

const processCourseComments = (comments) => {
    try {
        const commentMap = new Map();
        // Clone comments and initialize an empty replies array
        const clonedComments = comments.map(comment => ({
            ...comment,
            parentCommentId: comment.parentCommentId === "null" || comment.parentCommentId === null ? "" : comment.parentCommentId,
            replies: [],
        }));
        // Map comments by Id
        clonedComments.forEach(comment => {
            commentMap.set((comment.id && comment.id !== "null" && comment.id), comment);
        });

        // Attach replies to the **top-level parent** instead of nesting
        clonedComments.forEach(comment => {
            if (comment.parentCommentId && comment.parentCommentId.length && comment.parentCommentId !== "null") {
                let topLevelParent = commentMap.get(comment.parentCommentId);

                // Find the highest-level parent
                while (topLevelParent?.parentCommentId) {
                    topLevelParent = commentMap.get(topLevelParent.parentCommentId);
                }

                // Attach all replies to the top-level parent
                if (topLevelParent && topLevelParent.replies) {
                    topLevelParent.replies.push(comment);
                }
            }
        });

        // Return only top-level comments (those without `parentCommentId`)
        return clonedComments.filter(comment => !comment.parentCommentId || comment.parentCommentId === "null");
    } catch (error) {
        console.log("37 : Course Util : Error while processing comments:", error?.message);
        throw error;
    }
};

/**
 * formatViews - format a numeric view count into human-readable strings
 *
 * @param {number} count - the raw view count (e.g. 2354)
 * @param {object} [opts]
 * @param {number} [opts.decimalPlaces=2] - decimal places for the compact value
 * @param {string} [opts.style='compact'] - output style:
 *      'compact'      -> "2.35K Views"
 *      'raw'          -> "2354 Views"
 *      'both'         -> "2354 Views (2.35K)"
 *      'raw-in-suffix'-> "2354 Views in K"   (literal form as requested)
 * @returns {string}
 */
function formatViews(count, opts = {}) {
    try {
        const { decimalPlaces = 2, style = 'compact' } = opts;

        if (typeof count !== 'number' || !isFinite(count)) {
            return '0 Views';
        }

        const abs = Math.abs(count);
        if (abs < 1000) {
            // small numbers: show raw
            return `${count} Views`;
        }

        const units = ['K', 'M', 'B', 'T'];
        let unitIndex = -1;
        let compact = abs;

        while (compact >= 1000 && unitIndex < units.length - 1) {
            compact /= 1000;
            unitIndex++;
        }

        const suffix = units[unitIndex];            // 'K', 'M', ...
        // format compact value with requested decimal places, strip trailing zeros
        const compactStr = Number(compact.toFixed(decimalPlaces)).toString();

        switch (style) {
            case 'raw':
                return `${count} Views`;

            case 'both':
                return `${count} Views (${compactStr}${suffix})`;

            case 'raw-in-suffix':
                // literal style: "2354 Views in K"
                return `${count} Views in ${suffix}`;

            case 'compact':
            default:
                return `${compactStr}${suffix} Views`;
        }
    } catch (error) {
        console.log("98 : Course Util : Error while formatting views:", error?.message);
        return count;
    }
}

// formatting currency in INR
const formatCurrency = (value) => {
    return parseFloat(value).toLocaleString('en-IN', {
        maximumFractionDigits: 0,
        style: 'currency',
        currency: 'INR'
    });
}

const handleDiscountedPrice = (plan, convertToMonthly) => {
    try {
        const hasDiscount = plan?.discount?.hasDiscount;
        const discountPercent = plan?.discount?.discountPercent;
        let amountInRupees = Math.round(plan?.pricing?.price || 0);
        const discountValidFrom = plan?.discount?.validFrom;
        const discountValidTill = plan?.discount?.validTill;

        const duration = plan?.pricing?.billingCycle;

        if(duration && convertToMonthly) {
            if (duration === "3 Months") {
                amountInRupees = Math.round(amountInRupees / 3);
            } else if (duration === "6 Months") {
                amountInRupees = Math.round(amountInRupees / 6);
            } else if (duration === "9 Months") {
                amountInRupees = Math.round(amountInRupees / 9);
            }
        }

        if (!amountInRupees) {
            return {
                isValid: false,
                finalAmount: 0,
                formattedAmount: "₹ 0"
            };
        }
        let finalAmount = amountInRupees;
        let isValid = false;
        if (
            hasDiscount &&
            discountPercent > 0 &&
            discountValidFrom &&
            discountValidTill
        ) {
            const today = new Date().setHours(0, 0, 0, 0);
            const start = new Date(discountValidFrom).setHours(0, 0, 0, 0);
            const end = new Date(discountValidTill).setHours(0, 0, 0, 0);

            isValid = today >= start && today <= end;


            if (isValid) {
                finalAmount = Math.round(
                    amountInRupees - (amountInRupees * discountPercent) / 100
                );
            }
        }

        // Format into INR currency string
        const formattedAmount = formatCurrency(finalAmount);

        return {
            isValid,
            finalAmount,
            formattedAmount
        };

    } catch (error) {
        console.log("159 : Course Util : Error in handleDiscountedPrice => ", error);
        const fallback = plan?.pricing?.price || 0;
        return {
            finalAmount: fallback,
            formattedAmount: `₹ ${fallback.toLocaleString("en-IN")}`
        };
    }
};

const handleMonthlyPrice = (plan) => {
    try {
        let amountInRupees = plan?.pricing?.price;
        const duration = plan?.pricing?.billingCycle;

        if(duration) {
            if (duration === "3 Months") {
                amountInRupees = Math.round(amountInRupees / 3);
            } else if (duration === "6 Months") {
                amountInRupees = Math.round(amountInRupees / 6);
            } else if (duration === "9 Months") {
                amountInRupees = Math.round(amountInRupees / 9);
            }
        }

        if (!amountInRupees) {
            return {
                isValid: false,
                finalAmount: 0,
                formattedAmount: "₹ 0"
            };
        }
        let finalAmount = amountInRupees;
        
        // Format into INR currency string
        const formattedAmount = formatCurrency(finalAmount);

        return {
            finalAmount,
            formattedAmount
        };

    } catch (error) {
        console.log("159 : Course Util : Error in handleDMonthlyPrice => ", error);
        const fallback = plan?.pricing?.price || 0;
        return {
            finalAmount: fallback,
            formattedAmount: `₹ ${fallback.toLocaleString("en-IN")}`
        };
    }
};

const handleDiscountPrice = (plan) => {
    try {

        const amtUnit = plan?.amountUnit;
        const discountPercent = plan && plan?.notes && plan?.notes?.length ? plan?.notes[0]?.discountPercent : 0;
        const amount = plan && plan?.notes && plan?.notes?.length ? plan?.notes[0]?.price : 0;

        if (!amount) {
            return {
                finalAmount: 0,
                formattedAmount: "₹ 0",
                discountedAmount: 0
            };
        }

        let amountInRupees = amtUnit === PAISE ? amount / 100 : amount;
        let finalAmount = amountInRupees;
        if (discountPercent > 0) {
            finalAmount = Math.round(amountInRupees * discountPercent / 100);
            // Format into INR currency string
            const formattedAmount = formatCurrency(finalAmount);
            const discountedAmount = formatCurrency(amountInRupees - finalAmount);

            return {
                finalAmount,
                formattedAmount,
                discountedAmount
            };
        }
        return {
            finalAmount: 0,
            formattedAmount: "₹ 0",
            discountedAmount: formatCurrency(finalAmount)
        };

    } catch (error) {
        console.log("199 : Course Util : Error in handleDiscountedPrice => ", error);
        const fallback = 0;
        return {
            finalAmount: fallback,
            formattedAmount: `₹ ${fallback.toLocaleString("en-IN")}`,
            discountedAmount: 0
        };
    }
};

// Create a formatter for US English, using compact notation
const numberFormatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short' // 'short' (1M) or 'long' (1 million)
});

export { processCourseComments, formatViews, formatCurrency, handleDiscountedPrice, handleDiscountPrice, numberFormatter, handleMonthlyPrice }